/**
 * FormModal: the one dialog around a form (ComposedModal). The primary button is a real submit button tied to the form
 * by id, so Enter in a field and the button do the same thing; it shows InlineLoading while `pending`. Clicking
 * outside never closes it (the form could be lost). A server error shows in an InlineNotification (role alert) under
 * the form and takes focus so it is announced.
 *
 * Props: `title`, `label?`, `size?` (default md), `primaryText?` (default Save), `danger?`, `disabled?`, `pending`,
 * `error?` (translated message), `onSubmit()`, `onClose()`, `children` (the fields, rendered inside the <form>, which
 * has noValidate: the fields show their own validation).
 */
import {
  Button,
  ComposedModal,
  InlineLoading,
  InlineNotification,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from '@carbon/react';
import { useTranslation } from '@pos/i18n';
import { useEffect, useId, useRef, type ReactNode } from 'react';
import './components.scss';

export interface FormModalProps {
  title: string;
  label?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  primaryText?: string;
  danger?: boolean;
  disabled?: boolean;
  pending: boolean;
  error?: string | null;
  onSubmit(): void;
  onClose(): void;
  children: ReactNode;
}

export function FormModal({
  title,
  label,
  size = 'md',
  primaryText,
  danger,
  disabled,
  pending,
  error,
  onSubmit,
  onClose,
  children,
}: FormModalProps) {
  const { t } = useTranslation();
  const formId = useId();
  const errorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (error) errorRef.current?.focus();
  }, [error]);

  return (
    <ComposedModal open size={size} preventCloseOnClickOutside onClose={onClose}>
      <ModalHeader title={title} label={label} closeModal={onClose} iconDescription={t('carbon.close')} />
      <ModalBody hasForm>
        <form
          id={formId}
          noValidate
          className="pos-admin-form"
          onSubmit={(event) => {
            event.preventDefault();
            if (!disabled && !pending) onSubmit();
          }}
        >
          {children}
        </form>
        {error && (
          <div ref={errorRef} tabIndex={-1} className="pos-form-modal__error">
            <InlineNotification kind="error" role="alert" lowContrast hideCloseButton title={error} />
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button kind="secondary" onClick={onClose}>
          {t('common.cancel')}
        </Button>
        <Button kind={danger ? 'danger' : 'primary'} type="submit" form={formId} disabled={disabled || pending}>
          {pending ? <InlineLoading description={t('admin.saving')} /> : (primaryText ?? t('admin.save'))}
        </Button>
      </ModalFooter>
    </ComposedModal>
  );
}
